import { useTheme } from '@fluentui/react';
import { FC } from 'react';
import useReadableColor from '../../shared/hooks/useReadableColor';
import IconAndText from './IconAndText';

interface IconProps {
  text: string | number | boolean;
}

interface IconPropsWithThreshold extends IconProps {
  thresholdMet: boolean;
}

const useGreenColors = () => {
  const { palette: { green: icon } } = useTheme();
  const text = useReadableColor(icon);

  return {
    icon,
    text,
  };
};

const useYellowColors = () => {
  const { palette: { yellow: icon } } = useTheme();
  const text = useReadableColor(icon);

  return {
    icon,
    text,
  };
};

export const CompletedIcon: FC<IconProps> = ({ text }) => {
  const greenColors = useGreenColors();

  return (
    <IconAndText text={text}
      color={greenColors}
      icons={[
        { iconName: 'CircleRing' },
        { iconName: 'StatusCircleCheckmark', positionX: 0.55, positionY: 0.425 },
      ]}
    />
  );
};

export const AbandonedIcon: FC<IconProps> = ({ text }) => {
  const { palette } = useTheme();

  return (
    <IconAndText text={text}
      color={palette.red}
      icons={[
        { iconName: 'CircleRing' },
        { iconName: 'StatusCircleErrorX', positionX: 0.5375, positionY: 0.4675 },
      ]}
    />
  );
};

export const ConversationsWaiting: FC<IconPropsWithThreshold> = ({ text, thresholdMet }) => {
  const { palette } = useTheme();
  const yellowColors = useYellowColors();

  return (
    <IconAndText text={text}
      color={thresholdMet ? yellowColors : palette.redDark}
      icons={[{ iconName: thresholdMet ? 'CirclePause' : 'CirclePauseSolid' }]}
    />
  );
};

export const ConversationsConnected: FC<IconProps> = ({ text }) => {
  const { palette } = useTheme();

  return (
    <IconAndText text={text}
      color={palette.blue}
      icons={[
        { iconName: 'CircleRing' },
        { iconName: 'ConnectContacts', size: 0.6 },
      ]}
    />
  );
};

export const AgentsLoggedIn: FC<IconProps> = ({ text }) => {
  const { palette } = useTheme();

  return (
    <IconAndText text={text}
      color={palette.blue}
      icons={[{ iconName: 'Headset' }]}
    />
  );
};

export const AgentsBusy: FC<IconProps> = ({ text }) => {
  const { palette } = useTheme();

  return (
    <IconAndText text={text}
      color={palette.red}
      icons={[{ iconName: 'Headset' }]}
    />
  );
};

export const AgentsAvailable: FC<IconProps> = ({ text }) => {
  const greenColors = useGreenColors();

  return (
    <IconAndText text={text}
      color={greenColors}
      icons={[
        { iconName: 'Headset' },
        { iconName: 'CircleFill', size: 0.7, positionX: 0.75, positionY: 0.2, useBackgroundColor: true },
        { iconName: 'CircleFill', size: 0.6, positionX: 0.75, positionY: 0.2 },
        { iconName: 'StatusCircleCheckmark', size: 0.6, positionX: 0.7725, positionY: 0.16, useBackgroundColor: true },
      ]}
    />
  );
};

export const AgentsOnBreak: FC<IconProps> = ({ text }) => {
  const yellowColors = useYellowColors();

  return (
    <IconAndText text={text}
      color={yellowColors}
      icons={[
        { iconName: 'Headset' },
        { iconName: 'CircleFill', size: 0.7, positionX: 0.75, positionY: 0.2, useBackgroundColor: true },
        { iconName: 'CirclePauseSolid', size: 0.575, positionX: 0.75, positionY: 0.1975 },
      ]}
    />
  );
};

export const TasksHandled: FC<IconProps> = ({ text }) => {
  const greenColors = useGreenColors();

  return (
    <IconAndText text={text}
      color={greenColors}
      icons={[
        { iconName: 'WorkItem' },
        { iconName: 'SquareShapeSolid', size: 0.525, positionX: 0.7875, positionY: 0.825, useBackgroundColor: true },
        { iconName: 'StatusCircleCheckmark', size: 0.9, positionX: 0.825, positionY: 0.7375 },
      ]}
    />
  );
};

export const TasksMissed: FC<IconProps> = ({ text }) => {
  const { palette } = useTheme();

  return (
    <IconAndText text={text}
      color={palette.red}
      icons={[
        { iconName: 'WorkItem' },
        { iconName: 'SquareShapeSolid', size: 0.525, positionX: 0.7875, positionY: 0.825, useBackgroundColor: true },
        { iconName: 'StatusCircleErrorX', size: 0.9, positionX: 0.825, positionY: 0.785 },
      ]}
    />
  );
};

export const CallbackRequested: FC<IconProps> = ({ text }) => {
  const { palette } = useTheme();

  return (
    <IconAndText text={text}
      color={palette.blue}
      icons={[
        { iconName: 'CircleRing' },
        { iconName: 'Flag', size: 0.6 },
      ]}
    />
  );
};

export const QueueTimedOut: FC<IconProps> = ({ text }) => {
  const { palette } = useTheme();

  return (
    <IconAndText text={text}
      color={palette.red}
      icons={[
        { iconName: 'CircleRing' },
        { iconName: 'Timer', size: 0.6 },
      ]}
    />
  );
};

export const ConversationsProcessing: FC<IconProps> = ({ text }) => {
  const { palette } = useTheme();

  return (
    <IconAndText text={text}
      color={palette.blue}
      icons={[
        { iconName: 'CircleRing' },
        { iconName: 'Processing', size: 0.6 },
      ]}
    />
  );
};

export const BotFailed: FC<IconProps> = ({ text }) => {
  const { palette } = useTheme();

  return (
    <IconAndText text={text}
      color={palette.red}
      icons={[
        { iconName: 'CircleRing' },
        { iconName: 'Warning', size: 0.6, positionX: 0.52, positionY: 0.49 },
      ]}
    />
  );
};

export const RecordsIcon: FC<IconProps> = ({ text }) => {
  const { palette } = useTheme();

  return (
    <IconAndText
      text={text}
      color={palette.blue}
      icons={[{ iconName: 'PageList' }]}
    />
  );
};

export const NewIcon: FC<IconProps> = ({ text }) => {
  const yellowColors = useYellowColors();

  return (
    <IconAndText
      text={text}
      color={yellowColors}
      icons={[{ iconName: 'CirclePlus' }]}
    />
  );
};